import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Collapse,
  IconButton
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as ProcessingIcon,
  Cancel as CancelIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Undo as RollbackIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import RollbackConfirmationDialog from './RollbackConfirmationDialog';

// eslint-disable-next-line max-lines-per-function
function ImportProgress({ progress, onCancel }) {
  const { isPlatformAdmin } = useAuth();
  const [expandErrors, setExpandErrors] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
  const [startTime] = useState(Date.now());

  // Rollback state
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackError, setRollbackError] = useState(null);
  const [rollbackSuccess, setRollbackSuccess] = useState(false);

  // Update elapsed time
  useEffect(() => {
    if (progress.status === 'processing') {
      const timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setElapsedTime(elapsed);

        // Estimate remaining time
        if (progress.processed > 0 && progress.total > 0) {
          const rate = progress.processed / elapsed; // rows per second
          const remaining = progress.total - progress.processed;
          const estimatedSeconds = Math.ceil(remaining / rate);
          setEstimatedTimeRemaining(estimatedSeconds);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [progress.status, progress.processed, progress.total, startTime]);

  // Format time display
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes < 60) return `${minutes}m ${secs}s`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Calculate progress percentage
  const progressPercent = progress.total > 0
    ? Math.round((progress.processed / progress.total) * 100)
    : 0;

  // Calculate processing rate
  const processingRate = elapsedTime > 0
    ? (progress.processed / elapsedTime).toFixed(1)
    : 0;

  // Handle rollback confirmation
  const handleRollbackConfirm = async () => {
    try {
      setRollbackLoading(true);
      setRollbackError(null);

      const response = await api.rollbackImport(progress.importId);

      if (response.success) {
        setRollbackSuccess(true);
        setShowRollbackDialog(false);
        // Update progress status to reflect rollback
        if (typeof progress.onStatusUpdate === 'function') {
          progress.onStatusUpdate({
            ...progress,
            status: 'rolling_back'
          });
        }
      } else {
        throw new Error(response.message || 'Rollback failed');
      }
    } catch (error) {
      console.error('Rollback failed:', error);
      setRollbackError(error.message || 'Rollback operation failed');
    } finally {
      setRollbackLoading(false);
    }
  };

  // Get status color
  const getStatusColor = () => {
    switch (progress.status) {
      case 'processing': return 'primary';
      case 'rolling_back': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'warning';
      default: return 'primary';
    }
  };

  // Get status icon
  const getStatusIcon = () => {
    switch (progress.status) {
      case 'processing': return <ProcessingIcon color="primary" />;
      case 'rolling_back': return <RollbackIcon color="warning" />;
      case 'completed': return <CheckIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'cancelled': return <CancelIcon color="warning" />;
      default: return null;
    }
  };

  return (
    <Box>
      {/* Main Progress Card */}
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {getStatusIcon()}
              <Box>
                <Typography variant="h6">
                  {progress.status === 'processing' ? 'Import in Progress' :
                   progress.status === 'rolling_back' ? 'Rolling Back Import' :
                   progress.status === 'completed' ? 'Import Complete' :
                   progress.status === 'failed' ? 'Import Failed' :
                   progress.status === 'cancelled' ? 'Import Cancelled' :
                   'Preparing Import...'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Import ID: {progress.importId || 'Initializing...'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {progress.status === 'processing' && onCancel && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={onCancel}
                >
                  Cancel Import
                </Button>
              )}

              {/* Rollback button - show for completed imports with created entities */}
              {(progress.status === 'completed' || progress.status === 'failed') &&
               isPlatformAdmin &&
               progress.importId &&
               progress.successful > 0 && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<RollbackIcon />}
                  onClick={() => setShowRollbackDialog(true)}
                  disabled={rollbackLoading || rollbackSuccess}
                >
                  {rollbackSuccess ? 'Rolled Back' : 'Rollback Import'}
                </Button>
              )}
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                {progress.status === 'rolling_back'
                  ? `Deleting: ${progress.deletedCount || 0} of ${progress.successful || 0} entities`
                  : `Progress: ${progress.processed} of ${progress.total} rows`
                }
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {progress.status === 'rolling_back'
                  ? `${Math.round(((progress.deletedCount || 0) / (progress.successful || 1)) * 100)}%`
                  : `${progressPercent}%`
                }
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress.status === 'rolling_back'
                ? ((progress.deletedCount || 0) / (progress.successful || 1)) * 100
                : progressPercent
              }
              sx={{ height: 10, borderRadius: 5 }}
              color={getStatusColor()}
            />
          </Box>

          {/* Statistics Grid */}
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 6,
                md: 3
              }}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                <CheckIcon color="success" />
                <Typography variant="h4" color="success.main">
                  {progress.successful}
                </Typography>
                <Typography variant="caption">Successful</Typography>
              </Paper>
            </Grid>

            <Grid
              size={{
                xs: 6,
                md: 3
              }}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                <ErrorIcon color="error" />
                <Typography variant="h4" color="error.main">
                  {progress.failed}
                </Typography>
                <Typography variant="caption">Failed</Typography>
              </Paper>
            </Grid>

            <Grid
              size={{
                xs: 6,
                md: 3
              }}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                <TimerIcon color="action" />
                <Typography variant="h4">
                  {formatTime(elapsedTime)}
                </Typography>
                <Typography variant="caption">Elapsed Time</Typography>
              </Paper>
            </Grid>

            <Grid
              size={{
                xs: 6,
                md: 3
              }}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                <SpeedIcon color="primary" />
                <Typography variant="h4" color="primary.main">
                  {processingRate}
                </Typography>
                <Typography variant="caption">Rows/Second</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Time Estimates */}
          {progress.status === 'processing' && estimatedTimeRemaining && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Chip
                icon={<TimerIcon />}
                label={`Est. remaining: ${formatTime(estimatedTimeRemaining)}`}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
        </CardContent>
      </Card>
      {/* Current Processing Status */}
      {progress.status === 'processing' && (
        <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body1">
              Processing row {progress.processed + 1} of {progress.total}...
            </Typography>
          </Box>
          {progress.currentItem && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
              Current: {progress.currentItem}
            </Typography>
          )}
        </Paper>
      )}
      {/* Rollback Status */}
      {progress.status === 'rolling_back' && (
        <Paper elevation={2} sx={{ p: 2, mt: 3, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} color="warning" />
            <Typography variant="body1" color="text.primary">
              Rolling back import: Deleted {progress.deletedCount || 0} of {progress.successful || 0} entities
            </Typography>
          </Box>
          {progress.currentDeletion && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
              Currently deleting: {progress.currentDeletion}
            </Typography>
          )}
        </Paper>
      )}
      {/* Errors Section */}
      {progress.errors && progress.errors.length > 0 && (
        <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setExpandErrors(!expandErrors)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ErrorIcon color="error" />
              <Typography variant="subtitle1">
                Errors ({progress.errors.length})
              </Typography>
            </Box>
            <IconButton size="small">
              {expandErrors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={expandErrors}>
            <Divider sx={{ my: 2 }} />
            <List dense>
              {progress.errors.slice(0, 10).map((error, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <ErrorIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Row ${error.row}: ${error.message}`}
                    secondary={error.details || null}
                  />
                </ListItem>
              ))}
              {progress.errors.length > 10 && (
                <ListItem>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Typography variant="caption" color="text.secondary">
                        ...and {progress.errors.length - 10} more errors
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Collapse>
        </Paper>
      )}
      {/* Completion Messages */}
      {progress.status === 'completed' && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Import completed successfully!
          </Typography>
          <Typography variant="body2">
            Successfully imported {progress.successful} out of {progress.total} rows in {formatTime(elapsedTime)}.
          </Typography>
          {progress.failed > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {progress.failed} rows failed to import. Check the errors above for details.
            </Typography>
          )}
        </Alert>
      )}
      {progress.status === 'failed' && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Import failed!
          </Typography>
          <Typography variant="body2">
            The import process encountered a critical error and could not complete.
            {progress.errorMessage && (
              <Box sx={{ mt: 1 }}>
                <strong>Error:</strong> {progress.errorMessage}
              </Box>
            )}
          </Typography>
        </Alert>
      )}
      {progress.status === 'cancelled' && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Import cancelled
          </Typography>
          <Typography variant="body2">
            The import was cancelled after processing {progress.processed} out of {progress.total} rows.
            {progress.successful > 0 && (
              <Box sx={{ mt: 1 }}>
                {progress.successful} rows were successfully imported before cancellation.
              </Box>
            )}
          </Typography>
        </Alert>
      )}
      {/* Rollback Success */}
      {rollbackSuccess && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Rollback completed successfully!
          </Typography>
          <Typography variant="body2">
            All entities created during the import have been permanently deleted.
          </Typography>
        </Alert>
      )}
      {/* Rollback Error */}
      {rollbackError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Rollback failed!
          </Typography>
          <Typography variant="body2">
            {rollbackError}
          </Typography>
          <Button
            size="small"
            onClick={() => setRollbackError(null)}
            sx={{ mt: 1 }}
          >
            Dismiss
          </Button>
        </Alert>
      )}
      {/* Processing Tips */}
      {progress.status === 'processing' && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Import Tips:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>Large imports may take several minutes to complete</li>
            <li>Do not close this window while the import is in progress</li>
            <li>Failed rows can be re-imported after fixing the issues</li>
            <li>The import will continue in the background if you navigate away</li>
          </Box>
        </Alert>
      )}
      {/* Rollback Tips */}
      {progress.status === 'rolling_back' && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Rollback in Progress:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>This operation permanently deletes all entities created during import</li>
            <li>Related data (BigQuery datasets, GCS buckets) may become orphaned</li>
            <li>Do not close this window while rollback is in progress</li>
            <li>This operation cannot be undone once completed</li>
          </Box>
        </Alert>
      )}
      {/* Rollback Confirmation Dialog */}
      <RollbackConfirmationDialog
        open={showRollbackDialog}
        onClose={() => setShowRollbackDialog(false)}
        onConfirm={handleRollbackConfirm}
        importProgress={progress}
        createdEntities={progress.createdEntities || []}
        loading={rollbackLoading}
      />
    </Box>
  );
}

export default ImportProgress;